import { isArrayLike, observable, computed } from 'mobx';
import { models } from '@kurtosys/ksys-app-template';
import cultureSearchTransformReplace from '../utils/cultureSearchTransformReplace';
import { SearchEntity } from '@kurtosys/ksys-api-client/dist/requests/fund/SearchEntity';
import { SearchDocuments } from '@kurtosys/ksys-api-client/dist/requests/documents/SearchDocuments';
import { ListDisclaimers } from '@kurtosys/ksys-api-client/dist/requests/config/ListDisclaimers';
import { ListFundLists } from '@kurtosys/ksys-api-client/dist/requests/config/ListFundLists';
import { ListTranslations } from '@kurtosys/ksys-api-client/dist/requests/config/ListTranslations';
import { RetrieveDocument } from '@kurtosys/ksys-api-client/dist/requests/documents/RetrieveDocument';
import { RetrieveDocumentZip } from '@kurtosys/ksys-api-client/dist/requests/documents/RetrieveDocumentZip';
import { ListCultures } from '@kurtosys/ksys-api-client/dist/requests/config/ListCultures';
import { ListProperties } from '@kurtosys/ksys-api-client/dist/requests/config/ListProperties';
import { ListApplicationAssets } from '@kurtosys/ksys-api-client/dist/requests/applicationManager/ListApplicationAssets';
import { GetFilters as DocumentGetFilters } from '@kurtosys/ksys-api-client/dist/requests/documents/GetFilters';
import { GetFilters as FundGetFilters } from '@kurtosys/ksys-api-client/dist/requests/fund/GetFilters';
import { RetrieveClientConfiguration } from '@kurtosys/ksys-api-client/dist/requests/config/RetrieveClientConfiguration';
import { RetrieveUserConfiguration } from '@kurtosys/ksys-api-client/dist/requests/config/RetrieveUserConfiguration';
import { UpsertUserConfiguration } from '@kurtosys/ksys-api-client/dist/requests/config/UpsertUserConfiguration';
import { ExportPdf } from '@kurtosys/ksys-api-client/dist/requests/workflow/ExportPdf';
import { GetDataOptions } from '../models/services/GetDataOptions';
import { IListApplicationAssetsRequestBody } from '@kurtosys/ksys-api-client/dist/models/requests/applicationManager/IListApplicationAssetsRequestBody';
import { EntityType } from '../models/configuration/EntityType';

import { ListCommentaries } from '@kurtosys/ksys-api-client/dist/requests/config/ListCommentaries';

export class Services {
    @observable token: string = '';
    @observable explicitServiceUrl: string | undefined;
    apiUrl: string;
    entityType: EntityType;
    headers;

    constructor(entityType: EntityType, apiUrl: string, token: string) {
        this.entityType = entityType;
        // Setup development to pull from the proxy url in cases where we need to provide the base url for direct links,
        // the proxy does not proxy when accept headers are set to text/html.
        this.apiUrl =
            apiUrl ||
            (process.env.NODE_ENV === 'development' && this.proxyBaseUrl);
        this.token = token;
        this.headers = {
            'X-KSYS-TOKEN': token,
            'X-KSYS-EXPERIMENTAL-TRANSLATION': true,
        };
    }

    @computed
    get wrappedToken(): { value: string } {
        return { value: this.token };
    }

    @computed
    get serviceUrl() {
        let serviceUrl = this.explicitServiceUrl || `${this.apiUrl}/services/`;
        if (serviceUrl && serviceUrl.length > 0) {
            if (serviceUrl[serviceUrl.length - 1] !== '/') {
                serviceUrl += '/';
            }
        }
        return serviceUrl;
    }

    get proxyBaseUrl() {
        let proxyBaseUrl = process.env.REACT_APP_PROXY;
        if (proxyBaseUrl && proxyBaseUrl.length > 0) {
            if (proxyBaseUrl[proxyBaseUrl.length - 1] === '/') {
                proxyBaseUrl = proxyBaseUrl.substr(0, proxyBaseUrl.length - 2);
            }
        }
        return proxyBaseUrl;
    }

    getData = async (options: GetDataOptions) => {
        const {
            fundList,
            fundListExclusion,
            filterSearch,
            identifierSearch,
            sort,
            advancedSort,
            documentsJoinSpec,
            excludeExpiredDocuments,
            start,
            limit,
            cultureCode,
            sourceCulture,
            cultureSearchTransform,
            timeseriesInclude,
            statisticsInclude,
            allocationsInclude,
            disclaimersInclude,
            commentariesInclude,
            fundManagersInclude,
            clientCode = [''],
            include,
            translate = true,
            preserveOriginal = true,
            applyFormats = true,
            disclaimers = true,
            includeProperties,
        } = options;

        const cleanCultureCode = cultureSearchTransformReplace(
            cultureSearchTransform,
            cultureCode
        );
        const body = {
            type: this.entityType,
            clientCode,
            fundList,
            fundListExclusion,
            search: filterSearch,
            identifierSearch,
            sort,
            advancedSort,
            include: include || {
                timeseries: timeseriesInclude,
                statistics: statisticsInclude,
                allocations: allocationsInclude,
                disclaimers: disclaimersInclude,
                commentaries: commentariesInclude,
                fundManagers: fundManagersInclude,
            },
            limit,
            start,
            culture: cleanCultureCode,
            sourceCulture,
            translate,
            preserveOriginal,
            applyFormats,
            disclaimers,
            includeProperties,
        } as any;

        if (documentsJoinSpec) {
            body.include.documents = {
                joinSpec: documentsJoinSpec,
                excludeExpired: excludeExpiredDocuments,
            };
        }

        return this.getSearchEntity(body);
    };

    getDataSimplified = async (
        fundList,
        fundListExclusion,
        filterSearch,
        identifierSearch,
        include = undefined,
        includeProperties = undefined
    ) => {
        const body = {
            type: this.entityType,
            fundList,
            fundListExclusion,
            search: filterSearch,
            identifierSearch,
            include: include || {},
            preserveOriginal: true,
            includeProperties,
        } as any;

        return this.getSearchEntity(body);
    };

    getDocuments = async (search = {}) => {
        const body = {
            type: this.entityType,
            ...search,
        };

        return this.getSearchDocuments(body);
    };

    getFilters = async (
        filterProperties = [],
        search = [],
        fundList: string | Array<string> = '',
        fundListExclusion = []
    ) => {
        const body = {
            type: this.entityType,
            filterProperties,
            search,
            fundList: isArrayLike(fundList) ? fundList : [fundList],
            fundListExclusion,
        };

        return this.getFundGetFilters(body) as any;
    };

    getClientConfiguration = async (code: string) => {
        const body = {
            code,
        };
        const retrieveClientConfiguration = new RetrieveClientConfiguration(
            this.wrappedToken,
            {
                body,
                baseUrl: this.serviceUrl,
            }
        );
        return retrieveClientConfiguration.execute();
    };

    getDocumentFilters = async (codes = [], search = []) => {
        const body = {
            codes,
            search,
        };

        return this.getDocumentGetFilters(body) as any;
    };

    getTranslation = async (culture, translationCulture) => {
        const body = {
            culture,
            translationCulture,
        };

        return this.getListTranslations(body);
    };

    getProperties = async entityType => {
        const body = {
            entityType,
        };

        return this.getListProperties(body) as any;
    };

    documentDownload = async clientCode => {
        const body = {
            clientCode,
        };

        return this.getRetrieveDocument(body) as any;
    };

    documentDownloadZip = async documentCodes => {
        const body = {
            documentCodes,
        };

        return this.getRetrieveDocumentZip(body);
    };

    getRetrieveDocumentURL = (clientCode, fileName) => {
        let documentUrl = `${this.serviceUrl}documents/retrieveDocument/?token=${this.token}&clientCode=${clientCode}`;

        if (fileName) {
            const encodedFileName = encodeURIComponent(fileName);
            documentUrl += `&filename=${encodedFileName}`;
        }

        return documentUrl;
    };

    getCultures = async () => {
        const body = {};

        return this.getListCultures(body) as any;
    };

    getGlobalDisclaimers = async (disclaimerType?: Array<string>) => {
        // only fetch unlinked disclaimers
        const body: {
            global: boolean;
            disclaimerType?: Array<string>;
        } = { global: true };

        if (isArrayLike(disclaimerType) && disclaimerType.length > 0) {
            body.disclaimerType = disclaimerType;
        }

        return this.getListDisclaimers(body);
    };

    getGlobalCommentaries = async (commentaryType?: Array<string>) => {
        // only fetch unlinked disclaimers
        const body: {
            global: boolean;
            commentaryType?: Array<string>;
        } = { global: true };

        if (isArrayLike(commentaryType) && commentaryType.length > 0) {
            body.commentaryType = commentaryType;
        }

        return this.getListCommentaries(body);
    };

    getFundList = async (listName: string | Array<string>) => {
        const body: any = { useEntityAlias: false };

        // property naming differences between the listName array vs listname string
        if (isArrayLike(listName)) {
            body.listName = listName;
        } else {
            body.listname = listName;
        }

        return this.getListFundLists(body);
    };

    // api client calls
    getSearchEntity = async body => {
        const searchEntity = new SearchEntity(this.wrappedToken, {
            body,
            baseUrl: this.serviceUrl,
        });
        return searchEntity.execute();
    };

    getUserConfiguration = async (key: string): Promise<object> => {
        try {
            const retrieveUserConfiguration = new RetrieveUserConfiguration(
                undefined,
                {
                    body: {
                        code: key,
                    },
                    baseUrl: this.serviceUrl,
                }
            );
            return await retrieveUserConfiguration.execute();
        } catch (ex) {
            console.warn(ex);
        }
        return {};
    };

    setUserConfiguration = async (
        key: string,
        values: string
    ): Promise<boolean> => {
        try {
            const upsertUserConfiguration = new UpsertUserConfiguration(
                undefined,
                {
                    body: {
                        code: key,
                        config: values,
                    },
                    baseUrl: this.serviceUrl,
                }
            );
            await upsertUserConfiguration.execute();
            return true;
        } catch (ex) {
            console.warn(ex);
        }
        return false;
    };

    getSearchDocuments = async body => {
        const searchDocuments = new SearchDocuments(this.wrappedToken, {
            body,
            baseUrl: this.serviceUrl,
        });
        return searchDocuments.execute();
    };

    getListDisclaimers = async body => {
        const listDisclaimers = new ListDisclaimers(this.wrappedToken, {
            body,
            baseUrl: this.serviceUrl,
        });
        return listDisclaimers.execute();
    };

    getListCommentaries = async body => {
        const listCommentaries = new ListCommentaries(this.wrappedToken, {
            body,
            baseUrl: this.serviceUrl,
        });
        return listCommentaries.execute();
    };

    getListFundLists = async body => {
        const listFundLists = new ListFundLists(this.wrappedToken, {
            body,
            baseUrl: this.serviceUrl,
        });
        return listFundLists.execute();
    };

    getListTranslations = async body => {
        const listTranslations = new ListTranslations(this.wrappedToken, {
            body,
            baseUrl: this.serviceUrl,
        });
        return listTranslations.execute();
    };

    getRetrieveDocument = async body => {
        const retrieveDocument = new RetrieveDocument(this.wrappedToken, {
            body,
            baseUrl: this.serviceUrl,
        });
        return retrieveDocument.execute();
    };

    getRetrieveDocumentZip = async body => {
        const retrieveDocument = new RetrieveDocumentZip(this.wrappedToken, {
            body,
            baseUrl: this.serviceUrl,
        });
        return retrieveDocument.execute();
    };

    getDocumentGetFilters = async body => {
        const documentGetFilters = new DocumentGetFilters(this.wrappedToken, {
            body,
            baseUrl: this.serviceUrl,
        });
        return documentGetFilters.execute();
    };

    getFundGetFilters = async body => {
        const fundGetFilters = new FundGetFilters(this.wrappedToken, {
            body,
            baseUrl: this.serviceUrl,
        });
        return fundGetFilters.execute();
    };

    getListCultures = async body => {
        const listCultures = new ListCultures(this.wrappedToken, {
            body,
            baseUrl: this.serviceUrl,
        });
        return listCultures.execute();
    };

    getListProperties = async body => {
        const listProperties = new ListProperties(this.wrappedToken, {
            body,
            baseUrl: this.serviceUrl,
        });
        return listProperties.execute();
    };

    getListAssets = async (body: IListApplicationAssetsRequestBody) => {
        const listAssets = new ListApplicationAssets(this.wrappedToken, {
            body,
            baseUrl: this.serviceUrl,
        });
        return listAssets.execute();
    };

    generatePdf = async (body: models.portal.ExportToPDFRequest) => {
        const exportPdf = new ExportPdf(this.wrappedToken, {
            body,
            baseUrl: this.serviceUrl,
        });
        return exportPdf.callApi({
            body,
            disableCache: true,
            disableCachableRequests: true,
        });
    };
}



// WEBPACK FOOTER //
// ./src/services/services.ts